import React, { createContext, useContext, useState, ReactNode } from 'react';

interface CaseContextType {
    selectedCase: string | undefined;
    setSelectedCase: (newCase: string | undefined) => void;
}

const CaseContext = createContext<CaseContextType | undefined>(undefined);

export const CaseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [selectedCase, setSelectedCase] = useState<string | undefined>(() => {
        // Check if we're in the browser environment
        if (typeof window !== 'undefined') {
            // Try to get the selected case from localStorage
            const savedCase = localStorage.getItem('selectedCase');
            return savedCase || undefined;
        }
        return undefined;
    });

    // Update localStorage when selectedCase changes
    const updateSelectedCase = (newCase: string | undefined) => {
        setSelectedCase(newCase);
        if (typeof window !== 'undefined') {
            if (newCase) {
                localStorage.setItem('selectedCase', newCase);
            } else {
                localStorage.removeItem('selectedCase');
            }
            // Dispatch custom event for same-tab detection
            window.dispatchEvent(new Event('selectedCaseChanged'));
        }
    };

    return (
        <CaseContext.Provider value={{ selectedCase, setSelectedCase: updateSelectedCase }}>
            {children}
        </CaseContext.Provider>
    );
};

export const useCaseContext = (): CaseContextType => {
    const context = useContext(CaseContext);
    if (!context) {
        throw new Error('useCaseContext must be used within a CaseProvider');
    }
    return context;
};